/**
 * Autopromo Banner - Variant-Aware Price Display
 * Automatically updates discount banner when product variants change
 * Integrates with theme's eventBus system
 */

(function() {
  'use strict';

  // Prevent multiple initializations
  if (window.AutopromoBannerInitialized) {
    return;
  }
  window.AutopromoBannerInitialized = true;

  // Store all banner instances
  var bannerInstances = new Map();

  /**
   * Initialize a single autopromo banner
   */
  function initBanner(bannerId) {
    var container = document.getElementById(bannerId);
    if (!container) return null;

    // Check if already initialized
    if (bannerInstances.has(bannerId)) {
      return bannerInstances.get(bannerId);
    }

    var discountRate = parseFloat(container.dataset.discountRate || '0');
    if (!discountRate || discountRate <= 0) return null;

    var variantsScript = document.getElementById('autopromo-variants-' + bannerId.replace('autopromo-banner-', ''));
    if (!variantsScript) return null;

    var variants;
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      console.error('Autopromo: could not parse variants JSON', e);
      return null;
    }

    var moneyFormat = window.Shopify?.theme?.moneyFormat || window.Shopify?.money_format || '{{amount}}';

    function formatMoney(cents) {
      if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
        return Shopify.formatMoney(cents, moneyFormat);
      }
      var value = cents / 100;
      try {
        return value.toLocaleString(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD'
        });
      } catch (e) {
        return '$' + value.toFixed(2);
      }
    }

    function getCurrentVariantId() {
      // Try multiple selectors to find variant input
      var input = document.querySelector('form[action*="/cart/add"] [name="id"]') ||
                  document.querySelector('form[data-type="add-to-cart-form"] [name="id"]') ||
                  document.querySelector('.product-variant-id');
      if (!input) return null;
      var val = parseInt(input.value, 10);
      return isNaN(val) ? null : val;
    }

    function renderForCurrentVariant() {
      var variantId = getCurrentVariantId();
      if (!variantId) return;

      var variant = variants.find(function(v) { return v.id === variantId; });
      if (!variant) return;

      var finalPriceCents = Math.round(variant.price * (1 - discountRate));

      var finalPriceEl = container.querySelector('[data-final-price]');
      var originalPriceEl = container.querySelector('[data-original-price]');

      if (finalPriceEl) {
        finalPriceEl.textContent = formatMoney(finalPriceCents);
      }
      if (originalPriceEl) {
        originalPriceEl.textContent = formatMoney(variant.price);
      }
    }

    // Create instance object
    var instance = {
      container: container,
      render: renderForCurrentVariant,
      update: renderForCurrentVariant
    };

    // Initial render
    renderForCurrentVariant();

    // Store instance
    bannerInstances.set(bannerId, instance);

    return instance;
  }

  /**
   * Initialize all banners on the page
   */
  function initAllBanners() {
    var banners = document.querySelectorAll('[id^="autopromo-banner-"]');
    banners.forEach(function(banner) {
      initBanner(banner.id);
    });
  }

  /**
   * Update all banners
   */
  function updateAllBanners() {
    bannerInstances.forEach(function(instance) {
      if (instance && typeof instance.update === 'function') {
        instance.update();
      }
    });
  }

  /**
   * Set up event listeners
   */
  function setupEventListeners() {
    // Listen to theme's eventBus
    if (typeof window.eventBus !== 'undefined' && typeof window.eventBus.on === 'function') {
      window.eventBus.on('variant:updated', function(event) {
        setTimeout(updateAllBanners, 50); // Small delay to ensure DOM is updated
      });

      window.eventBus.on('variant:change', function(event) {
        setTimeout(updateAllBanners, 50);
      });
    }

    // Watch for variant input changes using MutationObserver
    function setupFormWatcher() {
      var form = document.querySelector('form[action*="/cart/add"]') || 
                 document.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) return null;

      var observer = new MutationObserver(function(mutations) {
        var shouldUpdate = false;
        mutations.forEach(function(mutation) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach(function(node) {
              if (node.nodeType === 1 && 
                  ((node.name === 'id' && node.type === 'hidden') ||
                   (node.classList && node.classList.contains('product-variant-id')))) {
                shouldUpdate = true;
              }
            });
          }
          if (mutation.type === 'attributes' && 
              mutation.attributeName === 'value' &&
              mutation.target.name === 'id') {
            shouldUpdate = true;
          }
        });
        if (shouldUpdate) {
          setTimeout(updateAllBanners, 10);
        }
      });

      observer.observe(form, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['value']
      });

      return observer;
    }

    var formObserver = setupFormWatcher();

    // Re-setup observer if form gets replaced
    var checkInterval = setInterval(function() {
      var form = document.querySelector('form[action*="/cart/add"]') || 
                 document.querySelector('form[data-type="add-to-cart-form"]');
      if (form && !formObserver) {
        formObserver = setupFormWatcher();
      }
    }, 500);

    // Clean up after 60 seconds
    setTimeout(function() {
      clearInterval(checkInterval);
    }, 60000);
  }

  /**
   * Initialize when DOM is ready
   */
  function initialize() {
    initAllBanners();
    setupEventListeners();
  }

  // Initialize immediately if DOM is ready, otherwise wait
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also initialize on page navigation (for SPA-like behavior)
  if (typeof window.Shopify !== 'undefined' && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(e) {
      setTimeout(initAllBanners, 100);
    });
  }

  // Re-initialize after cart operations (listen for cart drawer updates)
  if (typeof window.eventBus !== 'undefined' && typeof window.eventBus.on === 'function') {
    window.eventBus.on('update:cart:drawer', function() {
      setTimeout(function() {
        initAllBanners();
        updateAllBanners();
      }, 200);
    });
  }

  // Expose API for manual updates if needed
  window.AutopromoBanner = {
    init: initAllBanners,
    update: updateAllBanners,
    initBanner: initBanner
  };
})();